import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { fetchShipment } from '../../services/receivingAPI';
import {
  getCachedShipmentDetail,
  cacheShipmentDetail,
} from '../../services/shipmentCache';
import { ScannerSection } from '../scanner';
import { performOCR } from '../../services/visionAPI';
import { extractSKUFromText } from '../orders/orderdetail/scanningLogic';
import type { AirtableShipment, AirtableShipmentItem } from '../../types/airtableTypes';
import ShipmentDetailNavbar from './ShipmentDetailNavbar';
import './ShipmentDetail.css';

interface ShipmentItemBoxProps {
  item: AirtableShipmentItem;
  shipmentId: string;
  imageLoadFailed: boolean;
  onImageError: (itemId: string) => void;
}

function ShipmentItemBox({ item, shipmentId, imageLoadFailed, onImageError }: ShipmentItemBoxProps) {
  const location = useLocation();
  const showImage = Boolean(item.inventoryImageUrl) && !imageLoadFailed;

  return (
    <li>
      <Link
        className="shipment-item-box"
        to={`/receive/shipment/${shipmentId}/item/${item.id}`}
        state={location.state}
      >
        {showImage ? (
          <img
            src={item.inventoryImageUrl!}
            alt={item.name || item.sku}
            className="shipment-item-image"
            onError={() => onImageError(item.id)}
          />
        ) : (
          <div className="shipment-item-image-placeholder">No image</div>
        )}
        <div className="shipment-item-content">
          <span className="shipment-item-name">{item.name || 'Unnamed Item'}</span>
          {item.sku && <span className="shipment-item-sku">{item.sku}</span>}
          {item.shipmentName && (
            <span className="shipment-item-shipment-name">{item.shipmentName}</span>
          )}
          <span className="shipment-item-qty">
            Qty: {item.quantityShipped}
            {item.quantityRemaining !== null && ` (${item.quantityRemaining} remaining)`}
          </span>
          {item.purchaseOrderNumber && (
            <span className="shipment-item-po">{item.purchaseOrderNumber}</span>
          )}
        </div>
      </Link>
    </li>
  );
}

function filterShipmentItems(
  items: AirtableShipmentItem[],
  filterText: string
): AirtableShipmentItem[] {
  if (!filterText.trim()) {
    return items;
  }
  const searchTerm = filterText.toUpperCase();
  return items.filter((item) => {
    const sku = item.sku?.toUpperCase() ?? '';
    const name = item.name?.toUpperCase() ?? '';
    return sku.includes(searchTerm) || name.includes(searchTerm);
  });
}

function resolveSkuFilterFromOcr(
  ocrText: string,
  items: AirtableShipmentItem[]
): string {
  const stripped = extractSKUFromText(ocrText);
  const upperOcr = stripped.toUpperCase();
  const matchingSku = items
    .map((item) => item.sku?.trim())
    .filter((sku): sku is string => Boolean(sku))
    .find((sku) => upperOcr.includes(sku.toUpperCase()));
  return matchingSku ?? stripped;
}

function ShipmentDetail() {
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const isAllView = shipmentId === 'all';
  const [shipment, setShipment] = useState<AirtableShipment | null>(null);
  const [shipmentItems, setShipmentItems] = useState<AirtableShipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [brokenImageIds, setBrokenImageIds] = useState<ReadonlySet<string>>(() => new Set());
  const [webcamOn, setWebcamOn] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  const titleSegment = isAllView
    ? 'All Shipments'
    : shipment?.name
      ? `Shipment ${shipment.name}`
      : 'Shipment';
  usePageTitle(titleSegment);

  const filteredItems = useMemo(
    () => filterShipmentItems(shipmentItems, filterText),
    [shipmentItems, filterText]
  );

  useEffect(() => {
    setBrokenImageIds(new Set());
  }, [shipmentId]);

  useEffect(() => {
    let cancelled = false;

    const loadShipment = async () => {
      // Show cached data immediately for instant display (stale-while-revalidate)
      const cachedData = getCachedShipmentDetail(shipmentId!);
      if (cachedData) {
        setShipment(cachedData.shipment);
        setShipmentItems(cachedData.shipmentItems || []);
        setLoading(false);
      }

      // Always fetch fresh data in the background
      const data = await fetchShipment(shipmentId!);
      if (cancelled) return;
      
      if (!data) {
        if (!cachedData) {
          setError('Shipment not found');
        }
        setLoading(false);
        return;
      }
      
      setShipment(data.shipment);
      setShipmentItems(data.shipmentItems || []);
      cacheShipmentDetail(shipmentId!, data.shipment, data.shipmentItems || []);
      setLoading(false);
    };

    loadShipment();

    return () => { cancelled = true; };
  }, [shipmentId]);

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc) return false;
    setScanMessage('Processing image...');

    const ocrText = await performOCR(imageSrc);
    if (!ocrText) {
      setScanMessage('Error processing image. Please try again.');
      return false;
    }

    const detectedSku = extractSKUFromText(ocrText);
    if (!detectedSku) {
      setScanMessage('No SKU detected. Please try again.');
      return false;
    }

    const filterValue = resolveSkuFilterFromOcr(ocrText, shipmentItems);
    setFilterText(filterValue);
    setScanMessage(`Detected SKU: ${filterValue}`);
    return true;
  };

  const formatDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const etaFormatted = formatDate(shipment?.etaSF);
  const navbarTitle = isAllView ? 'All Shipments' : shipment?.name || 'Shipment';

  if (loading) {
    return (
      <div className="shipment-detail-container">
        <ShipmentDetailNavbar title={navbarTitle} />
        <p className="shipment-detail-status">Loading...</p>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="shipment-detail-container">
        <ShipmentDetailNavbar title={navbarTitle} />
        <p className="shipment-detail-status">{error || 'Shipment not found'}</p>
      </div>
    );
  }

  return (
    <div className="shipment-detail-container">
      <ShipmentDetailNavbar title={shipment.name} subtitle={etaFormatted ? `ETA: ${etaFormatted}` : undefined} />

      {shipment.description ? (
        <div className="shipment-detail-header">
          <p className="shipment-detail-description">{shipment.description}</p>
        </div>
      ) : null}

      {shipmentItems.length === 0 ? (
        <p style={{ color: '#aaa' }}>
          {isAllView ? 'No items in open shipments' : 'No items in this shipment'}
        </p>
      ) : (
        <>
          {isAllView && (
            <div className="shipment-all-scanner">
              <ScannerSection
                mode="barcode"
                onCapture={handleCapture}
                webcamOn={webcamOn}
                setWebcamOn={setWebcamOn}
                className="webcam-capture-container"
              />
              {scanMessage && (
                <p className="shipment-scan-message">{scanMessage}</p>
              )}
            </div>
          )}
          <div className="shipment-filter-container">
            <input
              type="text"
              className="shipment-filter-input"
              placeholder="Filter by SKU or name..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            {filterText && (
              <button
                className="shipment-filter-clear"
                onClick={() => setFilterText('')}
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
          {filteredItems.length === 0 ? (
            <p style={{ color: '#aaa' }}>No items match "{filterText}"</p>
          ) : (
            <ul className="shipment-items-list">
              {filteredItems.map((item) => (
                <ShipmentItemBox
                  key={item.id}
                  item={item}
                  shipmentId={shipmentId!}
                  imageLoadFailed={brokenImageIds.has(item.id)}
                  onImageError={(id) =>
                    setBrokenImageIds((prev) => {
                      const next = new Set(prev);
                      next.add(id);
                      return next;
                    })
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default ShipmentDetail;
